/**
 * Reports — the list (#239).
 *
 * Every report the caller may see, plus their own drafts (tagged). The SERVER
 * decides that set (GET /reports) — audience included — and nothing is
 * withheld here. The same `WigFilterPanel` chip bar the feedback queue uses
 * narrows it (name, status, category, audience, description; fields from
 * utils/reportsFilterFields), evaluated in the browser by `filterRows` — the
 * list arrives whole and small. One table, sorted by category then name.
 * Row click opens the viewer. BD.Admin also gets "New report" and "Data
 * sources" (/reports/sources).
 *
 * The grid's columns come from UI metadata (`table_name=broker_deal_reports`):
 * which columns exist, their labels, order, widths and visibility are data,
 * editable on Admin → Field Settings. Cells that are behaviour — the name
 * link, the audience tag, an empty-value dash — are render overrides keyed by
 * FIELD (utils/columnsFromMetadata). The server leaves the `audience` row out
 * for external users (and `audience` out of their summaries), so the Audience
 * column appears exactly when that row does, and the Audience filter exactly
 * when the rows carry `audience`.
 *
 * If the metadata cannot be loaded the list still works: it falls back to a
 * Name column alone, quietly, like the Feedback grid does when its column
 * metadata is unreachable.
 *
 * Gating is UI/UX only: every BD role may read reports, and the page
 * self-redirects without `reports.view`, like the other gated screens.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { Alert, Button, Row, Space, Tag, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";

import {
  WigAntLayout,
  WigAntTable,
  WigFilterPanel,
} from "@warwick-investment-group/wig-react-library";

import SignIn from "../signIn";
import { AppContext } from "../../AppContextProvider";
import { reportLayoutProps } from "./layout";
import usePermissions from "../../hooks/usePermissions";
import useReportsDataSource from "../../hooks/useReportsDataSource";
import reportsService from "../../services/reportsService";
import { apiErrorText } from "../../utils/apiError";
import dealsService from "../../services/dealsService";
import { columnsFromMetadata } from "../../utils/columnsFromMetadata";
import { WigReportAudienceTag } from "@warwick-investment-group/wig-react-library/reports";
import { filterFieldsToSearchFields } from "../../utils/filterFields";
import { filterRows } from "../../utils/filterQuery";
import {
  buildReportsFilterFields,
  sortReportsDefault,
} from "../../utils/reportsFilterFields";
import { readFlag, REPORTS_FILTERS_COLLAPSED, writeFlag } from "../../utils/uiPrefs";

const { Title, Text } = Typography;

/** The UI-metadata table describing this grid's columns. */
const REPORT_LIST_TABLE = "broker_deal_reports";

/** What the grid shows when its metadata cannot be loaded: the name alone. */
const FALLBACK_METADATA = [{ field: "name", label: "Name", data_type: "Text", ord: 1 }];

const dash = (v) => v || <Text type="secondary">—</Text>;

const EMPTY_QUERY = { combinator: "and", rules: [] };

const Reports = () => {
  const navigate = useNavigate();
  const { theme } = useContext(AppContext);
  const { can } = usePermissions();
  const allowed = can("reports.view");
  const canManage = can("reports.manage");
  const { call } = useReportsDataSource();

  // Column metadata; null while loading.
  const [columnMeta, setColumnMeta] = useState(null);

  // Applied filter-chip query, evaluated in the browser by `filterRows`.
  const [query, setQuery] = useState(EMPTY_QUERY);
  const [filtersCollapsed, setFiltersCollapsed] = useState(() =>
    readFlag(REPORTS_FILTERS_COLLAPSED, false)
  );
  const handleFiltersCollapsed = useCallback((next) => {
    setFiltersCollapsed(next);
    writeFlag(REPORTS_FILTERS_COLLAPSED, next);
  }, []);
  const handleFilterReset = useCallback(() => setQuery(EMPTY_QUERY), []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!allowed) navigate("/", { replace: true });
  }, [allowed, navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg(null);
      const resp = await call(reportsService.listReports);
      const data = resp?.data?.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setErrorMsg(apiErrorText(err));
    } finally {
      setLoading(false);
    }
  }, [call]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const resp = await call(dealsService.getMetadata, { tableName: REPORT_LIST_TABLE });
        const data = resp?.data?.data;
        if (!cancelled) setColumnMeta(Array.isArray(data) && data.length ? data : FALLBACK_METADATA);
      } catch (err) {
        // Deliberately not an error state (see the header): the reports are
        // still listed, by name.
        console.debug("Report list column metadata unavailable; showing names only:", err?.message || err);
        if (!cancelled) setColumnMeta(FALLBACK_METADATA);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed, call]);

  const columns = useMemo(
    () =>
      columnsFromMetadata(columnMeta || [], {
        renders: {
          name: (name, r) => (
            <Space size={6}>
              <a
                href={`/reports/${r.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/reports/${r.id}`);
                }}
              >
                {name}
              </a>
              {r.status === "draft" && <Tag color="gold">Draft</Tag>}
            </Space>
          ),
          category: dash,
          description: dash,
          audience: (v) => <WigReportAudienceTag audience={v} />,
        },
      }),
    [columnMeta, navigate]
  );

  const filterFields = useMemo(
    () => buildReportsFilterFields({ rows, columnMeta: columnMeta || [] }),
    [rows, columnMeta]
  );
  const advancedFields = useMemo(
    () => filterFieldsToSearchFields(filterFields),
    [filterFields]
  );

  const displayRows = useMemo(
    () => filterRows(sortReportsDefault(rows), query),
    [rows, query]
  );

  if (!allowed) return null;

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <Row>
          <WigAntLayout
            {...reportLayoutProps(theme === "dark")}
            content={
              <Space orientation="vertical" size={12} style={{ width: "100%" }}>
                <Row justify="space-between" align="middle">
                  <Title level={3} style={{ margin: 0 }}>
                    Reports
                  </Title>
                  {canManage && (
                    <Space>
                      <Button onClick={() => navigate("/reports/sources")}>
                        Report sources
                      </Button>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate("/reports/new")}
                      >
                        New report
                      </Button>
                    </Space>
                  )}
                </Row>
                {errorMsg && (
                  <Alert
                    type="error"
                    showIcon
                    title="Failed to load reports"
                    description={errorMsg}
                  />
                )}
                {(loading || rows.length > 0) && !errorMsg && (
                  <div style={{ width: "99%", padding: "4px 0" }}>
                    <WigFilterPanel
                      layout="inline"
                      fields={filterFields}
                      advancedFields={advancedFields}
                      fieldGroupLabel="Report"
                      // EMPTY_QUERY, not the live `query`: Reset restores to
                      // whatever `initialQuery` holds (see the feedback page).
                      initialQuery={EMPTY_QUERY}
                      onSearch={setQuery}
                      onReset={handleFilterReset}
                      collapsible
                      collapsed={filtersCollapsed}
                      onCollapsedChange={handleFiltersCollapsed}
                      resetLabel="Clear all"
                      status={
                        <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                          {loading
                            ? "Loading reports…"
                            : `${displayRows.length} report${
                                displayRows.length === 1 ? "" : "s"
                              } shown`}
                        </Text>
                      }
                    />
                  </div>
                )}
                <WigAntTable
                  dataSource={displayRows}
                  columns={columns}
                  rowKey="id"
                  loading={loading || columnMeta === null}
                  // The filter panel owns filtering, as on the feedback page.
                  showSearch={false}
                  showExport={false}
                  showActionButton={false}
                  enableColumnFilters={false}
                  pagination={{ defaultPageSize: 100, hideOnSinglePage: true }}
                  tableWidth="100%"
                  locale={{
                    emptyText: rows.length ? "No reports match your filters" : "No reports yet",
                  }}
                />
              </Space>
            }
          />
        </Row>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default Reports;
